import api from './client';
import type { ChannelTagCategory, ChannelTagCategoryRequest } from './channels';

// Admin API
export const adminAPI = {
  // User management
  createUser: (data: {
    email: string;
    name: string;
    password: string;
    username?: string;
    role?: 'member' | 'admin' | 'super_admin';
    organizationId?: string;  // UUID
    organizationRole?: 'member' | 'admin';
  }) => api.post('/admin/users', data),

  getAllUsers: (params?: { search?: string; role?: string; status?: string; verified?: 'true' | 'false'; createdAfter?: string; createdBefore?: string; sortBy?: string; sortOrder?: 'asc' | 'desc'; page?: number; limit?: number }) =>
    api.get('/admin/users', { params }),

  getUserById: (userId: string) => api.get(`/admin/users/${userId}`),

  updateUserRole: (userId: string, role: 'member' | 'admin' | 'super_admin') =>
    api.put(`/admin/users/${userId}/role`, { role }),

  updateUserStatus: (userId: string, status: 'active' | 'suspended') =>
    api.put(`/admin/users/${userId}/status`, { status }),

  deleteUser: (userId: string) => api.delete(`/admin/users/${userId}`),

  forcePasswordReset: (userId: string) => api.post(`/admin/users/${userId}/force-password-reset`),

  setUserPassword: (userId: string, password: string) =>
    api.post(`/admin/users/${userId}/set-password`, { password }),

  verifyUserEmail: (userId: string) =>
    api.put(`/admin/users/${userId}/verify-email`),

  // Fast user switch (Spec 079)
  toggleFastSwitch: (userId: string, enabled: boolean) =>
    api.patch(`/admin/users/${userId}/fast-switch`, { enabled }),

  // Admin impersonation (Spec 197)
  impersonateUser: (userId: string, reason: string) =>
    api.post(`/admin/users/${userId}/impersonate`, { reason }),

  // Audit logs
  getAuditLogs: (params?: { page?: number; limit?: number; adminId?: string; targetUserId?: string; action?: string }) =>
    api.get('/admin/audit-logs', { params }),

  // Auth audit log (Spec 309) — distinct from getAuditLogs' general admin_audit_logs
  // table; this is the dedicated authentication-event log (auth_audit_log).
  getAuthAuditLog: (params?: { userId?: number; eventType?: string; startDate?: string; endDate?: string; limit?: number; offset?: number }) =>
    api.get('/admin/auth-audit', { params }),

  purgeAuthAuditLog: () => api.post('/admin/auth-audit/purge'),

  // Organization management
  getAllOrganizations: (params?: { search?: string; subscriptionLevel?: string; memberCountMin?: number; memberCountMax?: number; createdAfter?: string; createdBefore?: string; sortBy?: string; sortOrder?: 'asc' | 'desc'; page?: number; limit?: number }) =>
    api.get('/admin/organizations', { params }),

  createOrganization: (data: {
    name: string;
    description?: string;
    memberUserIds?: string[];
    adminUserIds?: string[];
  }) => api.post('/admin/organizations', data),

  getUserOrganizations: (userId: string) => api.get(`/admin/users/${userId}/organizations`),

  addUserToOrganization: (userId: string, organizationId: string, role?: 'member' | 'admin') =>
    api.post(`/admin/users/${userId}/organizations`, { organizationId, role }),

  removeUserFromOrganization: (userId: string, orgId: string) =>
    api.delete(`/admin/users/${userId}/organizations/${orgId}`),

  updateUserOrganizationRole: (userId: string, orgId: string, role: 'member' | 'admin') =>
    api.put(`/admin/users/${userId}/organizations/${orgId}/role`, { role }),

  deleteOrganization: (orgId: string) => api.delete(`/admin/organizations/${orgId}`),

  getOrganizationMembers: (orgId: string) => api.get(`/admin/organizations/${orgId}/members`),

  // Storage management (super admin only)
  getStorageConfig: () => api.get('/admin/storage/config'),

  getStorageStats: () => api.get('/admin/storage/stats'),

  getPendingUploads: () => api.get('/admin/storage/pending'),

  retryPendingUpload: (id: string) => api.post(`/admin/storage/pending/${id}/retry`),

  cancelPendingUpload: (id: string, deleteFile?: boolean) =>
    api.delete(`/admin/storage/pending/${id}`, { data: { deleteFile } }),

  getStorageErrors: (limit?: number) =>
    api.get('/admin/storage/errors', { params: { limit } }),

  getOrgAuditLog: (orgId: string, limit?: number) =>
    api.get(`/admin/storage/audit/${orgId}`, { params: { limit } }),

  getTempFiles: () => api.get('/admin/storage/temp-files'),

  deleteTempFile: (filename: string) =>
    api.delete(`/admin/storage/temp-files/${encodeURIComponent(filename)}`),

  cleanupTempFiles: (maxAgeHours?: number) =>
    api.post('/admin/storage/cleanup', { maxAgeHours }),

  // Dashboard metrics
  getDashboardOverview: () => api.get('/admin/dashboard/overview'),

  // 2026-08-15 Admin Panel red-team fix, MEDIUM: the backend groups these
  // charts' daily buckets by calendar day IN THE PASSED TIMEZONE (defaults
  // to UTC if omitted/invalid) -- send the viewing admin's own zone so a
  // signup/task/upload near UTC midnight attributes to the right local day.
  getDashboardUsers: (range?: number) =>
    api.get('/admin/dashboard/users', { params: { range, tz: Intl.DateTimeFormat().resolvedOptions().timeZone } }),

  getDashboardTasks: (range?: number) =>
    api.get('/admin/dashboard/tasks', { params: { range, tz: Intl.DateTimeFormat().resolvedOptions().timeZone } }),

  getDashboardMedia: () =>
    api.get('/admin/dashboard/media', { params: { tz: Intl.DateTimeFormat().resolvedOptions().timeZone } }),

  getDashboardRevenue: () => api.get('/admin/dashboard/revenue'),

  getDashboardMetrics: (start?: string, end?: string) =>
    api.get('/admin/dashboard/metrics', { params: { start, end } }),

  backfillMetrics: (startDate: string, endDate: string) =>
    api.post('/admin/dashboard/backfill', { startDate: startDate, endDate: endDate }),

  triggerAggregation: () => api.post('/admin/dashboard/aggregate'),

  // Advanced dashboards (super admin only)
  getRevenueDetails: () => api.get('/admin/dashboard/revenue-details'),
  getSystemHealth: () => api.get('/admin/dashboard/system-health'),
  getAuditSummary: () => api.get('/admin/dashboard/audit-summary'),

  // Monitoring dashboard (Spec 310, super admin only) — over the pre-existing
  // backend/src/routes/admin/monitoringRoutes.ts, zero new backend routes.
  getMonitoringDashboard: (hours?: number) =>
    api.get('/admin/monitoring/dashboard', { params: { hours } }),
  getMonitoringMetrics: (hours?: number) =>
    api.get('/admin/monitoring/metrics', { params: { hours } }),
  getMonitoringAlerts: (limit?: number) =>
    api.get('/admin/monitoring/alerts', { params: { limit } }),
  acknowledgeMonitoringAlert: (alertId: number) =>
    api.post(`/admin/monitoring/alerts/${alertId}/acknowledge`),
  sendTestAlert: () => api.post('/admin/monitoring/alerts/test'),
  getSlowQueries: () => api.get('/admin/monitoring/slow-queries'),
  getMonitoringDatabase: () => api.get('/admin/monitoring/database'),
  flushMonitoringMetrics: () => api.post('/admin/monitoring/flush-metrics'),
  getMediaWorkerStatus: () => api.get('/admin/monitoring/media-workers'),

  // Feature Flags (super admin only)
  getFeatureFlags: () => api.get('/admin/feature-flags'),
  getFeatureFlag: (name: string) => api.get(`/admin/feature-flags/${name}`),
  createFeatureFlag: (data: { name: string; description?: string }) =>
    api.post('/admin/feature-flags', data),
  updateFeatureFlag: (name: string, data: {
    enabled?: boolean;
    rolloutPercentage?: number;
    allowedOrgIds?: number[];
    allowedUserIds?: number[];
    description?: string;
    requiredSubscriptionLevel?: string | null;
  }) => api.put(`/admin/feature-flags/${name}`, data),
  deleteFeatureFlag: (name: string) => api.delete(`/admin/feature-flags/${name}`),
  toggleFeatureFlag: (name: string) => api.post(`/admin/feature-flags/${name}/toggle`),
  getFeatureFlagHistory: (name: string, limit?: number) =>
    api.get(`/admin/feature-flags/${name}/history`, { params: { limit } }),

  // Feature Limits (super admin only)
  getFeatureLimits: () => api.get('/admin/feature-limits'),
  getFeatureLimitsTiers: () => api.get('/admin/feature-limits/tiers'),
  getFeatureLimit: (name: string) => api.get(`/admin/feature-limits/${name}`),
  createFeatureLimit: (data: {
    featureName: string;
    freeLimit: number | null;
    paidLimit: number | null;
    premiumLimit: number | null;
    limitType?: 'count' | 'megabytes' | 'days';
    scope?: 'organization' | 'user';
    enforcementMode?: 'hard' | 'soft' | 'disabled';
    description?: string;
  }) => api.post('/admin/feature-limits', data),
  updateFeatureLimit: (name: string, data: {
    freeLimit?: number | null;
    paidLimit?: number | null;
    premiumLimit?: number | null;
    leaderLimit?: number | null;
    limitType?: 'count' | 'megabytes' | 'days';
    scope?: 'organization' | 'user';
    enforcementMode?: 'hard' | 'soft' | 'disabled';
    description?: string;
  }) => api.put(`/admin/feature-limits/${name}`, data),
  deleteFeatureLimit: (name: string) => api.delete(`/admin/feature-limits/${name}`),
  getFeatureLimitHistory: (name: string, limit?: number) =>
    api.get(`/admin/feature-limits/${name}/history`, { params: { limit } }),
  invalidateFeatureLimitsCache: () => api.post('/admin/feature-limits/invalidate-cache'),

  // Selection endpoints (lightweight for dropdowns)
  // search (optional): server-side ILIKE on name/email, capped at 50 results —
  // without it, falls back to the unfiltered top-500 alphabetical snapshot.
  getUsersForSelect: (search?: string) =>
    search ? api.get('/admin/users/select', { params: { search } }) : api.get('/admin/users/select'),
  getOrganizationsForSelect: () => api.get('/admin/organizations/select'),

  // Organization subscription management
  getOrganizationsWithSubscriptions: (params?: { search?: string; planSlug?: string }) =>
    api.get('/admin/organizations/subscriptions', { params }),

  getSubscriptionPlans: () => api.get('/admin/subscription-plans'),

  grantSubscription: (orgId: string, data: { planSlug: string; startDate: string; endDate: string }) =>
    api.post(`/admin/organizations/${orgId}/subscription`, data),

  revokeSubscription: (orgId: string) =>
    api.delete(`/admin/organizations/${orgId}/subscription`),

  // Age verification (admin only)
  overrideAgeVerification: (userId: string, notes?: string) =>
    api.post('/age-verification/admin/override', { userId, notes }),
  getGracePeriodUsers: (status?: 'expiring' | 'expired' | 'all') =>
    api.get('/age-verification/admin/grace-period-users', { params: { status } }),

  // Site banner management (super admin only)
  getSiteBanner: () => api.get('/admin/site-banner'),
  createSiteBanner: (data: {
    message: string;
    bannerType?: 'info' | 'warning' | 'danger';
    linkUrl?: string;
    linkText?: string;
    dismissible?: boolean;
    expiresAt?: string;
  }) => api.post('/admin/site-banner', data),
  updateSiteBanner: (id: number, data: {
    message?: string;
    bannerType?: 'info' | 'warning' | 'danger';
    linkUrl?: string;
    linkText?: string;
    dismissible?: boolean;
    expiresAt?: string;
  }) => api.put(`/admin/site-banner/${id}`, data),
  deactivateSiteBanner: (id: number) => api.delete(`/admin/site-banner/${id}`),

  // AI Content Moderation (super admin only — Spec 078)
  getAIModerationQueue: (params?: { status?: string; category?: string; tier?: number; page?: number; limit?: number }) =>
    api.get('/admin/moderation/queue', { params }),
  getAIModerationDetail: (id: number) => api.get(`/admin/moderation/queue/${id}`),
  resolveAIModeration: (id: number, data: { action: 'approve' | 'remove' | 'warn' | 'ban'; reason?: string }) =>
    api.put(`/admin/moderation/queue/${id}/resolve`, data),
  getAIModerationStats: () => api.get('/admin/moderation/stats'),
  getAIModerationLog: (params?: { contentType?: string; authorId?: string; finalAction?: string; dateFrom?: string; dateTo?: string; page?: number; limit?: number }) =>
    api.get('/admin/moderation/log', { params }),
  updateAIModerationThresholds: (key: string, value: unknown) =>
    api.put('/admin/moderation/thresholds', { key, value }),

  // Trust Level moderation UI (Spec 308 — frontend for the pre-existing
  // Spec 022 backend, mounted at /api/admin/trust-levels, requireSuperAdmin).
  getUserTrustLevel: (userId: string) => api.get(`/admin/trust-levels/users/${userId}`),
  overrideUserTrustLevel: (userId: string, level: 0 | 1 | 2 | 3, reason?: string) =>
    api.put(`/admin/trust-levels/users/${userId}`, { level, reason }),
  warnUserTrustLevel: (userId: string, reason: string) =>
    api.post(`/admin/trust-levels/users/${userId}/warn`, { reason }),
  promoteUserToModerator: (userId: string) =>
    api.post(`/admin/trust-levels/users/${userId}/promote-moderator`),
  revokeUserModerator: (userId: string, reason: string) =>
    api.post(`/admin/trust-levels/users/${userId}/revoke-moderator`, { reason }),
  getTrustLevelRateLimits: () => api.get('/admin/trust-levels/rate-limits'),
  updateTrustLevelRateLimits: (
    level: 0 | 1 | 2 | 3 | 4,
    updates: Partial<{
      dailyUploads: number;
      dailyDmInitiations: number;
      maxCommunityJoins: number;
      canCreateCommunity: boolean;
      canApplyModerator: boolean;
      canAccessModQueue: boolean;
    }>
  ) => api.put(`/admin/trust-levels/rate-limits/${level}`, updates),
};

// Organization Categories Admin API (super_admin only)
export const organizationCategoryAdminAPI = {
  // Get all categories with counts
  getAll: () => api.get('/admin/organization-categories'),

  // Get active categories only
  getActive: () => api.get('/admin/organization-categories/active'),

  // Get category statistics
  getStats: () => api.get('/admin/organization-categories/stats'),

  // Get single category
  get: (id: number) => api.get(`/admin/organization-categories/${id}`),

  // Create new category
  create: (data: {
    slug: string;
    name: string;
    description?: string;
    icon?: string;
    sortOrder?: number;
  }) => api.post('/admin/organization-categories', data),

  // Update category
  update: (id: number, data: {
    slug?: string;
    name?: string;
    description?: string;
    icon?: string;
    sortOrder?: number;
  }) => api.put(`/admin/organization-categories/${id}`, data),

  // Reorder categories
  reorder: (categoryIds: number[]) =>
    api.post('/admin/organization-categories/reorder', { categoryIds }),

  // Deactivate category
  deactivate: (id: number) =>
    api.post(`/admin/organization-categories/${id}/deactivate`),

  // Reactivate category
  reactivate: (id: number) =>
    api.post(`/admin/organization-categories/${id}/reactivate`),

  // Delete category (only if no organizations use it)
  delete: (id: number) => api.delete(`/admin/organization-categories/${id}`),
};

// Spec 283: Channel Self-Identification Tags — channels admin request queue.
// "Channels admin" reuses requireSuperAdmin as-is (no separate role exists —
// see spec Engineering Requirements § Middleware Chain naming note).
export const channelTagAdminAPI = {
  getPendingRequests: () =>
    api.get<{ success: boolean; data: ChannelTagCategoryRequest[] }>('/admin/tag-category-requests'),

  approveRequest: (id: number) =>
    api.post<{ success: boolean; data: ChannelTagCategory }>(`/admin/tag-category-requests/${id}/approve`),

  rejectRequest: (id: number, reason?: string) =>
    api.post<{ success: boolean; data: ChannelTagCategory }>(`/admin/tag-category-requests/${id}/reject`, { reason }),
};
